/** エージェント連携。Windows 常駐エージェントのトークンと、Mac(Apple MDM)の登録プロファイル。 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Card, Flex, Form, Input, Space, Statistic, Table, Tag, Typography, Upload, message } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useEffect, useState } from "react";

import { useApi } from "../../lib/useApi";
import { jst } from "../../lib/format";
import { AgentDistCard, UnboundAgentTokens } from "./AgentDistCards";
import {
  errorText,
  useDownload,
  useInvalidateMdm,
  useMdmPerms,
  useMdmSetting,
  useMdmWebhookStatus,
} from "./hooks";

const { Paragraph, Text } = Typography;

type AgentForm = { token?: string };
type MdmForm = {
  topic?: string;
  server_url?: string;
  certs_dir?: string;
};

/** 32 バイトの乱数を hex(64 文字)にしたトークン。サーバーには発行の口を作っていない。 */
function newToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** 伏字(•••設定済み)のまま保存しても上書きされない(core.settings_service の規則)。 */
function useSaveSetting(key: string, done: () => void) {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.saveMdmSetting(key, data),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した)");
      invalidate();
      done();
    },
    onError: (e) => void message.error(errorText(e)),
  });
}

function AgentTokenCard() {
  const query = useMdmSetting("agent");
  const [form] = Form.useForm<AgentForm>();
  // 生成した直後だけ平文で見せる(保存済みの値は伏字のまま返ってくるので見えない)
  const [generated, setGenerated] = useState("");
  const save = useSaveSetting("agent", () => {
    setGenerated("");
    void query.refetch();
  });

  // 生成した平文を、遅れて届いた GET の伏字で上書きしない。読み込み前に「生成」を押すと
  // 応答が後から着いて欄が伏字に戻り、保存が「変更なし」になる(2026-10-02 e2e で再現)
  useEffect(() => {
    if (query.data && !generated) form.setFieldsValue({ token: String(query.data.value.token ?? "") });
  }, [query.data, form, generated]);

  const generate = () => {
    const token = newToken();
    form.setFieldsValue({ token });
    setGenerated(token);
  };

  return (
    <Card size="small" title="共有トークン(旧方式)">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="旧方式。新しい端末は上の登録コードで入れる"
        description="全端末が同じ 1 本を持つため、漏れると全端末分が開き、変えると全端末が止まる。端末トークンへ移し終えたら空にして保存すると、旧方式の口は閉じる(fail-closed)。"
      />
      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="設定を取得できなかった"
          description={errorText(query.error)}
        />
      )}
      {!query.isLoading && !query.isError && !query.data?.value.token && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="共有トークンは未設定(旧方式の口は閉じている)"
          description="端末トークン(登録コード)で動く端末には影響しない。旧方式の端末が残っているときだけ設定する。"
        />
      )}
      {generated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="保存すると今のエージェントは 401 になるので、install し直すこと"
          description={
            <span>
              この値が見えるのは今だけ(保存すると伏字になる)。控えてから保存する:{" "}
              <Text code copyable={{ text: generated }}>
                {generated}
              </Text>
            </span>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => save.mutate({ token: v.token ?? "" })}
        disabled={save.isPending}
      >
        <Form.Item
          name="token"
          label="token"
          extra="旧方式の端末は X-Agent-Token にこの値を付ける。空なら旧方式は 401(端末トークンは通る)"
        >
          <Input maxLength={200} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={save.isPending}>
            保存
          </Button>
          <Button onClick={generate}>トークンを生成</Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            最終更新 {jst(query.data?.updated_at ?? null)}
          </Text>
        </Space>
      </Form>
    </Card>
  );
}

/**
 * 開いている端末の種類。登録の手順は OS で違う(iPhone / iPad は **Safari でしか**
 * プロファイルを受け取れない)ので、案内をこれで出し分ける。
 * iPadOS の Safari は既定で Mac を名乗るので、タッチ対応の Macintosh は iPad とみなす。
 */
type Client = "mac" | "ios" | "ios-other-browser" | "other";

/** Safari 以外のブラウザ・アプリ内ブラウザ(LINE / Slack / Facebook / Instagram など)の UA 断片。 */
const NOT_SAFARI = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|Line\/|Slack|FBAN|FBAV|Instagram|Twitter|MicroMessenger|Gmail/i;

/**
 * iOS / iPadOS の Safari だと**確信できるときだけ** `ios` を返す(MOB1 fix1)。
 * アプリ内ブラウザ(WKWebView)は `Version/… Safari/` を付けないことが多いので、
 * それが揃っていない・他のブラウザの印があるときは注意を出す側に倒す。
 */
function isSafari(ua: string, ipadAsMac: boolean): boolean {
  if (NOT_SAFARI.test(ua)) return false;
  if (!/Version\/[\d.]+/.test(ua) || !/Safari\//.test(ua)) return false;
  // デスクトップ表示の iPad Safari は Mobile/ を付けない
  return ipadAsMac || /Mobile\//.test(ua);
}

function detectClient(): Client {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  const ipadAsMac = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
  if (/iPhone|iPad|iPod/.test(ua) || ipadAsMac) {
    return isSafari(ua, ipadAsMac) ? "ios" : "ios-other-browser";
  }
  if (/Macintosh/.test(ua)) return "mac";
  return "other";
}

/** 登録の手順(OS ごと)。発行ボタンの下に出す。 */
function EnrollGuide() {
  const client = detectClient();
  return (
    <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 12 }}>
      {client === "ios-other-browser" && (
        <Alert
          type="error"
          showIcon
          message="Safari で開いていないかもしれない"
          description="Chrome などのブラウザや、LINE・Slack などのアプリの中で開いたページはプロファイルを受け取れない。このページの URL を Safari で開き直す。"
        />
      )}
      <Alert
        type={client === "ios" ? "info" : undefined}
        showIcon={client === "ios"}
        data-testid="enroll-guide-ios"
        message="iPhone / iPad"
        description={
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>このページを端末の <b>Safari</b> で開き、「登録プロファイルを発行」を押す(Chrome は不可)</li>
            <li>「許可」を押してダウンロードする</li>
            <li>設定 App を開き、上に出る「プロファイルがダウンロードされました」から「インストール」</li>
            <li>Apple Watch を使っている場合は、インストール先に「iPhone」を選ぶ</li>
            <li>パスコードを入れて「リモート管理」を信頼する</li>
          </ol>
        }
      />
      <Alert
        type={client === "mac" ? "info" : undefined}
        showIcon={client === "mac"}
        message="Mac"
        description="発行したファイルを開き、システム設定 → 一般 → デバイス管理(プロファイル)からインストールする。"
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        Safari で落とせない場合は、Mac で発行したファイルを AirDrop で送っても同じ手順で入る。
        非監視の iPhone / iPad は再起動など一部のコマンドが使えない(Apple の制限)。
      </Text>
    </Space>
  );
}

/** Apple の登録プロファイル。発行は mdm:operator から、設定の読み書きは mdm:admin だけ。 */
function MacProfileCard({ canAdmin }: { canAdmin: boolean }) {
  const query = useMdmSetting("mdm", canAdmin);
  const [form] = Form.useForm<MdmForm>();
  const save = useSaveSetting("mdm", () => void query.refetch());
  const download = useDownload();

  useEffect(() => {
    if (!query.data) return;
    const v = query.data.value;
    form.setFieldsValue({
      topic: String(v.topic ?? ""),
      server_url: String(v.server_url ?? ""),
      certs_dir: String(v.certs_dir ?? ""),
    });
  }, [query.data, form]);

  return (
    <Card
      size="small"
      title="Mac / iPhone / iPad(Apple MDM)"
      extra={
        <Button onClick={() => void download("/api/mdm/enroll.mobileconfig")}>
          登録プロファイルを発行
        </Button>
      }
    >
      <EnrollGuide />
      {!canAdmin && (
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          発行だけできる(接続先・証明書の設定を見るには mdm:admin が要る)。
        </Paragraph>
      )}
      {canAdmin && (
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => save.mutate({ ...v })}
          disabled={save.isPending}
        >
          <Form.Item name="topic" label="topic" extra="APNs のトピック(com.apple.mgmt.External.…)">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="server_url"
            label="server_url"
            extra="Mac から見た受け口。https のみ(例 https://itmgmt-mdm.automagic-system.com)"
          >
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item
            name="certs_dir"
            label="certs_dir"
            extra="ca.pem / ca.key を置いたディレクトリ。既定はコンテナ内の /certs(compose がマウント)。開発機のパスが入っていると本番で発行に失敗する"
          >
            <Input maxLength={500} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              保存
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              最終更新 {jst(query.data?.updated_at ?? null)}
            </Text>
          </Space>
        </Form>
      )}
    </Card>
  );
}

/**
 * APNs の push 証明書。PEM の証明書と秘密鍵を NanoMDM にそのまま渡す。
 *
 * **中身はこのサーバーに残さない。** 成功したら NanoMDM が読み取った Topic だけを
 * 設定 `mdm.topic` に控える(登録プロファイルがこれを使う)。
 */
function PushCertCard() {
  const api = useApi();
  const invalidate = useInvalidateMdm();
  const query = useMdmSetting("mdm");
  const [cert, setCert] = useState<UploadFile[]>([]);
  const [key, setKey] = useState<UploadFile[]>([]);

  const upload = useMutation({
    mutationFn: async () => {
      const c = cert[0]?.originFileObj;
      const k = key[0]?.originFileObj;
      if (!c || !k) throw new Error("証明書(.pem)と秘密鍵(.key)の2つを選ぶ");
      return api.uploadPushCert(await c.text(), await k.text());
    },
    onSuccess: (res) => {
      void message.success(`push 証明書を入れた(topic: ${res.topic || "戻りに無し"})`);
      setCert([]);
      setKey([]);
      invalidate();
      void query.refetch();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const topic = String(query.data?.value.topic ?? "");

  return (
    <Card size="small" title="APNs の push 証明書">
      {!topic && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="push 証明書が未設定。Mac に何も送れない"
          description="Apple の Push Certificates Portal で作った証明書と、その秘密鍵(PEM)を入れる。"
        />
      )}
      {topic && (
        <Paragraph style={{ marginBottom: 12 }}>
          いまの Topic: <Text code>{topic}</Text>
        </Paragraph>
      )}
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Upload
          accept=".pem,.crt,.cer"
          maxCount={1}
          beforeUpload={() => false}
          fileList={cert}
          onChange={(info) => setCert(info.fileList)}
        >
          <Button>証明書(PEM)を選ぶ</Button>
        </Upload>
        <Upload
          accept=".pem,.key"
          maxCount={1}
          beforeUpload={() => false}
          fileList={key}
          onChange={(info) => setKey(info.fileList)}
        >
          <Button>秘密鍵(PEM)を選ぶ</Button>
        </Upload>
        <Space>
          <Button type="primary" loading={upload.isPending} onClick={() => upload.mutate()}>
            NanoMDM に入れる
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            入れ替えると、既に登録済みの Mac は再登録が要る
          </Text>
        </Space>
      </Space>
    </Card>
  );
}

/**
 * NanoMDM からの Webhook をいくつ受けて、いくつ無視したか。
 *
 * **無視した件数が見えないと、台帳に端末が出ない原因を掴めない**(2026-09-28 の実機
 * 登録で「関門で無視 → 200 を返す」が黙って起きた)。端末の値(UDID・製造番号)は
 * 数えていないので、ここには理由と件数しか出ない。
 */
function WebhookStatusCard() {
  const query = useMdmWebhookStatus();
  const recent = query.data?.recent;
  const total = query.data?.total;
  const reasons = recent?.ignored_reasons ?? [];

  return (
    <Card
      size="small"
      title={`Webhook の受け口(直近 ${query.data?.window_hours ?? 24} 時間・時間単位。いまの時間帯を含む)`}
      extra={
        <Text type="secondary" style={{ fontSize: 12 }}>
          集計 {jst(query.data?.generated_at ?? null)}
        </Text>
      }
    >
      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="件数を取得できなかった"
          description={errorText(query.error)}
        />
      )}
      {query.data && !query.data.signing_key_set && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="MDM_WEBHOOK_HMAC_KEY が未設定。Webhook は 503 で受け付けていない"
          description=".env に値を入れて api と NanoMDM を再起動する(画面からは設定できない)。"
        />
      )}
      {!!recent?.ignored && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`無視した Webhook が ${recent.ignored} 件ある`}
          description="下の理由を見る。UDID の欠落・不一致なら端末にプロファイルを入れ直す。"
        />
      )}
      <Flex gap={32} wrap style={{ marginBottom: reasons.length ? 12 : 0 }}>
        <Statistic title="取り込んだ" value={recent?.ok ?? 0} suffix={`/ 累計 ${total?.ok ?? 0}`} />
        <Statistic
          title="重複(同じ通知の再送)"
          value={recent?.duplicate ?? 0}
          suffix={`/ 累計 ${total?.duplicate ?? 0}`}
        />
        <Statistic
          title="無視"
          value={recent?.ignored ?? 0}
          suffix={`/ 累計 ${total?.ignored ?? 0}`}
          valueStyle={recent?.ignored ? { color: "#d46b08" } : undefined}
        />
        <Statistic
          title="失敗(サーバー側)"
          value={recent?.error ?? 0}
          suffix={`/ 累計 ${total?.error ?? 0}`}
          valueStyle={recent?.error ? { color: "#cf1322" } : undefined}
        />
      </Flex>
      {reasons.length > 0 && (
        <Table
          scroll={{ x: "max-content" }}
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={reasons}
          columns={[
            { title: "無視した理由", width: 320, ellipsis: true, dataIndex: "label" },
            {
              title: "件数",
              dataIndex: "count",
              width: 110,
              render: (v: number) => <Tag color="orange">{v}</Tag>,
            },
          ]}
        />
      )}
    </Card>
  );
}

export function AgentsPage() {
  const { canAdmin, canView, canWrite, hasWindows } = useMdmPerms();

  return (
    <div>
      <Typography.Title level={3}>エージェント連携</Typography.Title>

      <Card size="small" title="チェックインの手順" style={{ marginBottom: 16 }}>
        <Paragraph style={{ marginBottom: 8 }}>
          Windows は常駐エージェントが定期的に
          <Text code>POST /api/mdm/agent/checkin</Text>
          を叩いて、ホスト名・製造番号・インストール済みアプリを送り、配布するレジストリを受け取る。
          入れ方は下の「Windows エージェントの配布と登録」で exe を落とし、登録コードを発行して、
          端末の管理者 PowerShell で表示された <Text code>install</Text> コマンドを実行する。
          端末は登録コードを端末ごとのトークンに引き換え、以後はそのトークンで認証する。
          自己更新もこのサーバーから(エージェント認証付きで)受け取る。
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          Mac は同梱の NanoMDM が受け、端末のやりとりごとに Webhook が
          <Text code>POST /api/mdm/nanomdm/webhook</Text>
          へ直接届く(<Text code>server/scripts/mdm_sync.py</Text>{" "}
          はオフラインの持ち込み用に残してあるだけ)。旧エージェント向けに{" "}
          <Text code>POST /agent/checkin</Text> も残してある。
        </Paragraph>
      </Card>

      {!canAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="配布物・登録コード・トークンの閲覧と変更には mdm:admin が要る"
          description={
            canWrite ? "登録プロファイルの発行は mdm:operator でもできる。" : undefined
          }
        />
      )}

      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {canView && <WebhookStatusCard />}
        {canAdmin && hasWindows && <AgentDistCard />}
        {canAdmin && hasWindows && <UnboundAgentTokens />}
        {canAdmin && <AgentTokenCard />}
        {canAdmin && <PushCertCard />}
        {canWrite && <MacProfileCard canAdmin={canAdmin} />}
      </Space>
    </div>
  );
}
